import { useEffect, useState } from "react";
import { Box, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import {
    Field, FormInput, FormSelect, FormSwitch, FormCheckbox, FormTextarea,
    CollapsibleSection, ItemCard, ItemHeader, AddButton, Divider, SubsectionTitle,
} from "@/components/jobOffer/ui";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { CampaignService } from "@/services/CampaignService";
import { CHANNEL_OPTIONS } from "@/components/campaign/channelOptions";
import { LEAD_STATUS_OPTIONS } from "@/components/campaign/leadStatusOptions";
import { createEmptyFollowUpAttempt, createEmptyOutputIntegration } from "@/models/campaign";
import GoogleSheetsConnectPanel from "@/components/campaign/GoogleSheetsConnectPanel";
import MicrosoftExcelConnectPanel from "@/components/campaign/MicrosoftExcelConnectPanel";

const DELAY_PRESETS = [
    { value: "0", label: "Natychmiast" },
    { value: "240", label: "Po 4 godzinach" },
    { value: "1440", label: "Po 24 godzinach (następnego dnia)" },
    { value: "4320", label: "Po 3 dniach" },
    { value: "10080", label: "Po tygodniu" },
];
const DELAY_OPTIONS = [...DELAY_PRESETS, { value: "custom", label: "Inne (podaj liczbę minut)" }];
const PRESET_VALUES = new Set(DELAY_PRESETS.map(p => p.value));

const STOP_CONDITION_OPTIONS = [
    { value: "LeadResponded", label: "Lead odpowiedział na wiadomość" },
    { value: "LeadConverted", label: "Lead dokonał zakupu/konwersji" },
    { value: "MeetingBooked", label: "Spotkanie zostało umówione" },
    { value: "StatusChangedManually", label: "Status leada zmienił się ręcznie" },
    { value: "LeadRequestedNoContact", label: "Lead poprosił o brak kontaktu" },
];

const TARGET_SYSTEM_OPTIONS = [
    { value: "GoogleSheets", label: "Google Sheets" },
    { value: "Excel", label: "Microsoft Excel / 365" },
    { value: "TwentyCrm", label: "Twenty CRM" },
    { value: "HubSpot", label: "HubSpot" },
    { value: "Pipedrive", label: "Pipedrive" },
    { value: "Salesforce", label: "Salesforce" },
    { value: "Airtable", label: "Airtable" },
    { value: "Erp", label: "ERP" },
    { value: "CrmClient", label: "CRM Klienta" },
    { value: "Api", label: "API" },
    { value: "Webhook", label: "Webhook" },
];

const TRIGGER_OPTIONS = [
    { value: "AfterEachResponse", label: "Po każdej odpowiedzi" },
    { value: "AfterStatusChange", label: "Po zmianie statusu" },
    { value: "AfterQualification", label: "Po kwalifikacji" },
    { value: "AfterConversationEnd", label: "Po zakończeniu rozmowy" },
    { value: "AfterMeetingBooked", label: "Po umówieniu spotkania" },
    { value: "AfterConversion", label: "Po konwersji" },
];

const ACTION_OPTIONS = [
    { value: "UpdateExistingRecord", label: "Aktualizuj istniejący rekord" },
    { value: "CreateNewRecord", label: "Utwórz nowy rekord" },
];

// Mirrors ResolveLeadFieldValue's switch in OutputIntegrationSyncBackgroundService.cs -- keep in sync
// manually, there's no shared enum backing these "source field" string keys.
const FIXED_SOURCE_FIELDS = [
    { value: "LeadId", label: "Lead ID" },
    { value: "Firstname", label: "Imię" },
    { value: "Lastname", label: "Nazwisko" },
    { value: "Company", label: "Firma" },
    { value: "JobTitle", label: "Stanowisko" },
    { value: "PhoneNumber", label: "Telefon" },
    { value: "Email", label: "E-mail" },
    { value: "Status", label: "Status" },
    { value: "Source", label: "Źródło" },
    { value: "CreatedAt", label: "Data utworzenia" },
    { value: "UpdatedAt", label: "Data aktualizacji" },
];

const channelLabel = (value) => CHANNEL_OPTIONS.find(o => o.value === value)?.label ?? value;
const statusLabel = (value) => LEAD_STATUS_OPTIONS.find(o => o.value === value)?.label ?? value;

const ResultRow = ({ label, value }) => (
    <HStack justify="space-between" gap={4} py={1.5} borderBottom="1px solid" borderColor="border.primary">
        <Text fontSize="sm" color="text.grey">{label}</Text>
        <Text fontSize="sm" color="text.white" fontWeight={500} textAlign="right">{value}</Text>
    </HStack>
);

const Step8FollowUpOutput = ({ values, setValues, forceOpen }) => {
    const set = (key, val) => setValues({ ...values, [key]: val });

    // --- Follow-up ---
    const followUp = values.followUpConfig ?? { attempts: [], stopConditions: [] };
    const setFollowUp = (patch) => set("followUpConfig", { ...followUp, ...patch });
    const attempts = followUp.attempts ?? [];
    const setAttempts = (next) => setFollowUp({ attempts: next });
    const updateAttempt = (i, patch) => setAttempts(attempts.map((a, idx) => idx === i ? { ...a, ...patch } : a));
    const removeAttempt = (i) => setAttempts(attempts.filter((_, idx) => idx !== i));
    const addAttempt = () => setAttempts([...attempts, createEmptyFollowUpAttempt()]);
    const stopConditions = followUp.stopConditions ?? [];
    const toggleStopCondition = (value, checked) => setFollowUp({
        stopConditions: checked ? [...stopConditions, value] : stopConditions.filter(c => c !== value),
    });

    // --- Output integrations ---
    const [serviceAccountEmail, setServiceAccountEmail] = useState(null);
    const [excelConnectedAccountEmails, setExcelConnectedAccountEmails] = useState({});

    useEffect(() => {
        CampaignService.getLeadSourceServiceAccountEmail()
            .then((data) => setServiceAccountEmail(data?.email ?? null))
            .catch(() => {});
    }, []);

    const checkAccess = async (spreadsheetUrl, sheetName) => {
        try {
            const data = await CampaignService.checkLeadSourceAccess(spreadsheetUrl, sheetName);
            return { isConnected: !!data?.isConnected, columns: data?.columns ?? [], error: data?.error ?? null };
        } catch (error) {
            return { isConnected: false, columns: [], error: extractBusinessErrorMessage(error) ?? "Nie udało się zweryfikować dostępu." };
        }
    };

    const checkExcelAccess = async (fileUrl, worksheetName, connectionId) => {
        try {
            const data = await CampaignService.checkExcelLeadSourceAccess(fileUrl, worksheetName, connectionId);
            return { isConnected: !!data?.isConnected, columns: data?.columns ?? [], error: data?.error ?? null };
        } catch (error) {
            return { isConnected: false, columns: [], error: extractBusinessErrorMessage(error) ?? "Nie udało się zweryfikować dostępu." };
        }
    };

    const integrations = values.outputIntegrations ?? [];
    const setIntegrations = (next) => set("outputIntegrations", next);
    const updateIntegration = (i, patch) => setIntegrations(integrations.map((o, idx) => idx === i ? { ...o, ...patch } : o));
    const removeIntegration = (i) => setIntegrations(integrations.filter((_, idx) => idx !== i));
    const addIntegration = () => setIntegrations([...integrations, createEmptyOutputIntegration()]);

    const availableFields = [
        ...FIXED_SOURCE_FIELDS,
        ...(values.customFieldMappings ?? [])
            .filter(c => c.fieldName)
            .map(c => ({ value: c.fieldName, label: c.fieldName })),
    ];

    const targetFieldFor = (integration, sourceField) =>
        (integration.fieldMapping ?? []).find(m => m.sourceField === sourceField)?.targetField ?? "";
    const setTargetFieldFor = (integrationIndex, sourceField, targetField) => {
        const integration = integrations[integrationIndex];
        const rest = (integration.fieldMapping ?? []).filter(m => m.sourceField !== sourceField);
        updateIntegration(integrationIndex, { fieldMapping: targetField ? [...rest, { sourceField, targetField }] : rest });
    };

    // --- Test simulation ---
    const [testContact, setTestContact] = useState("");
    const [isRunning, setIsRunning] = useState(false);
    const [result, setResult] = useState(null);
    const campaignId = values.id;

    const runSimulation = async () => {
        if (!testContact.trim()) {
            toaster.create({ type: "error", title: "Podaj telefon lub e-mail testowy" });
            return;
        }
        setIsRunning(true);
        setResult(null);
        try {
            setResult(await CampaignService.simulate(campaignId, testContact.trim()));
        } catch (error) {
            toaster.create({ type: "error", title: extractBusinessErrorMessage(error) ?? "Nie udało się uruchomić symulacji" });
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <VStack w="full" align="stretch" gap={4}>
            <CollapsibleSection title="Follow-up" subtitle="Kolejne próby kontaktu, jeśli lead nie odpowiedział, i kiedy je zatrzymać — opcjonalne" defaultOpen forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    {attempts.map((a, i) => {
                        const delayValue = String(a.delayMinutes ?? 0);
                        const isPreset = PRESET_VALUES.has(delayValue);
                        return (
                            <ItemCard key={a.id ?? i}>
                                <ItemHeader title={`Próba ${i + 1}`} index={i} onRemove={() => removeAttempt(i)} />
                                <VStack align="stretch" gap={3}>
                                    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4}>
                                        <Field label="Opóźnienie">
                                            <FormSelect
                                                value={isPreset ? delayValue : "custom"}
                                                onChange={(v) => updateAttempt(i, { delayMinutes: v === "custom" ? a.delayMinutes : Number(v) })}
                                                options={DELAY_OPTIONS}
                                                placeholder="Wybierz opóźnienie"
                                            />
                                        </Field>
                                        <Field label="Kanał">
                                            <FormSelect value={a.channel ?? ""} onChange={(v) => updateAttempt(i, { channel: v || null })} options={CHANNEL_OPTIONS} placeholder="Wybierz kanał" />
                                        </Field>
                                    </Box>
                                    {!isPreset && (
                                        <Field label="Liczba minut">
                                            <FormInput type="number" min="0" value={a.delayMinutes ?? 0} onChange={(e) => updateAttempt(i, { delayMinutes: e.target.value === "" ? 0 : Number(e.target.value) })} />
                                        </Field>
                                    )}
                                    <Field label="Wiadomość follow-up (opcjonalnie)">
                                        <FormTextarea value={a.messageTemplate ?? ""} onChange={(e) => updateAttempt(i, { messageTemplate: e.target.value })} rows={2} />
                                    </Field>
                                </VStack>
                            </ItemCard>
                        );
                    })}
                    <AddButton label="Dodaj próbę follow-up" onClick={addAttempt} />

                    <Divider my={1} />
                    <SubsectionTitle>Warunki zatrzymania follow-up</SubsectionTitle>
                    <VStack align="stretch" gap={3}>
                        {STOP_CONDITION_OPTIONS.map((opt) => (
                            <FormCheckbox key={opt.value} label={opt.label} checked={stopConditions.includes(opt.value)} onChange={(checked) => toggleStopCondition(opt.value, checked)} />
                        ))}
                    </VStack>
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Systemy docelowe" subtitle="Gdzie mają trafiać wyniki rozmów — zalecane, ale nie wymagane do aktywacji" forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    {integrations.map((o, i) => {
                        const isGoogleSheets = o.targetSystem === "GoogleSheets";
                        const isExcel = o.targetSystem === "Excel";
                        return (
                            <ItemCard key={o.id ?? i}>
                                <ItemHeader title={TARGET_SYSTEM_OPTIONS.find(t => t.value === o.targetSystem)?.label || o.targetSystem} index={i} onRemove={() => removeIntegration(i)} />
                                <VStack align="stretch" gap={3}>
                                    <FormSwitch label="Aktywne" checked={!!o.isActive} onChange={(v) => updateIntegration(i, { isActive: v })} />
                                    <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4}>
                                        <Field label="System docelowy">
                                            <FormSelect value={o.targetSystem} onChange={(v) => updateIntegration(i, { targetSystem: v })} options={TARGET_SYSTEM_OPTIONS} placeholder="Wybierz system" />
                                        </Field>
                                        <Field label="Wyzwalacz">
                                            <FormSelect value={o.trigger} onChange={(v) => updateIntegration(i, { trigger: v })} options={TRIGGER_OPTIONS} placeholder="Wybierz wyzwalacz" />
                                        </Field>
                                    </Box>
                                    <Field label="Akcja">
                                        <FormSelect value={o.action} onChange={(v) => updateIntegration(i, { action: v })} options={ACTION_OPTIONS} placeholder="Wybierz akcję" />
                                    </Field>

                                    {isGoogleSheets && (
                                        <>
                                            <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4}>
                                                <Field label="URL arkusza wynikowego">
                                                    <FormInput value={o.spreadsheetUrl ?? ""} onChange={(e) => updateIntegration(i, { spreadsheetUrl: e.target.value, isConnected: false })} placeholder="https://docs.google.com/spreadsheets/d/..." />
                                                </Field>
                                                <Field label="Nazwa zakładki">
                                                    <FormInput value={o.sheetName ?? ""} onChange={(e) => updateIntegration(i, { sheetName: e.target.value, isConnected: false })} placeholder="np. Wyniki" />
                                                </Field>
                                            </Box>
                                            <GoogleSheetsConnectPanel
                                                serviceAccountEmail={serviceAccountEmail}
                                                spreadsheetUrl={o.spreadsheetUrl}
                                                sheetName={o.sheetName}
                                                isConnected={!!o.isConnected}
                                                onCheckAccess={checkAccess}
                                                onConnected={() => updateIntegration(i, { isConnected: true })}
                                            />
                                            <Field label="Kolumna identyfikująca lead" hint='Nagłówek kolumny z Lead ID (domyślnie "Lead ID")'>
                                                <FormInput value={o.keyTargetColumn ?? ""} onChange={(e) => updateIntegration(i, { keyTargetColumn: e.target.value })} placeholder="Lead ID" />
                                            </Field>
                                        </>
                                    )}

                                    {isExcel && (
                                        <>
                                            <Box display="grid" gridTemplateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap={4}>
                                                <Field label="Link do pliku Excel">
                                                    <FormInput value={o.excelFileUrl ?? ""} onChange={(e) => updateIntegration(i, { excelFileUrl: e.target.value, isConnected: false })} placeholder="https://onedrive.live.com/... lub https://firma.sharepoint.com/..." />
                                                </Field>
                                                <Field label="Nazwa arkusza (worksheet)">
                                                    <FormInput value={o.excelWorksheetName ?? ""} onChange={(e) => updateIntegration(i, { excelWorksheetName: e.target.value, isConnected: false })} placeholder="np. Wyniki" />
                                                </Field>
                                            </Box>
                                            <MicrosoftExcelConnectPanel
                                                campaignId={values.id}
                                                purpose={`Output:${o.id}`}
                                                fileUrl={o.excelFileUrl}
                                                worksheetName={o.excelWorksheetName}
                                                connectionId={o.excelConnectionId}
                                                connectedAccountEmail={excelConnectedAccountEmails[o.id]}
                                                isConnected={!!o.isConnected}
                                                onCheckAccess={checkExcelAccess}
                                                onAccountConnected={({ connectionId, connectedAccountEmail }) => {
                                                    updateIntegration(i, { excelConnectionId: connectionId, isConnected: false });
                                                    setExcelConnectedAccountEmails((prev) => ({ ...prev, [o.id]: connectedAccountEmail ?? null }));
                                                }}
                                                onConnected={() => updateIntegration(i, { isConnected: true })}
                                            />
                                            <Field label="Kolumna identyfikująca lead" hint='Nagłówek kolumny z Lead ID (domyślnie "Lead ID")'>
                                                <FormInput value={o.keyTargetColumn ?? ""} onChange={(e) => updateIntegration(i, { keyTargetColumn: e.target.value })} placeholder="Lead ID" />
                                            </Field>
                                        </>
                                    )}

                                    <Box borderTopWidth={1} borderColor="border.primary" pt={3} mt={1}>
                                        <Box fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                            Mapowanie pól wyjściowych
                                        </Box>
                                        <VStack align="stretch" gap={2}>
                                            {availableFields.map((f) => (
                                                <Box key={f.value} display="flex" alignItems="center" gap={4}>
                                                    <Box fontSize="sm" color="text.white" flex={1} minW={0} title={f.label} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                        {f.label}
                                                    </Box>
                                                    <Box flex={1}>
                                                        <FormInput value={targetFieldFor(o, f.value)} onChange={(e) => setTargetFieldFor(i, f.value, e.target.value)} placeholder='np. "AI Status" -- puste = nie eksportowane' />
                                                    </Box>
                                                </Box>
                                            ))}
                                        </VStack>
                                    </Box>
                                </VStack>
                            </ItemCard>
                        );
                    })}
                    <AddButton label="Dodaj wyjście" onClick={addIntegration} />
                </VStack>
            </CollapsibleSection>

            <CollapsibleSection title="Symulacja" subtitle={`Test pełnego procesu dla kampanii: ${values.name || "(bez nazwy)"} — opcjonalne narzędzie testowe`} forceOpen={forceOpen}>
                <VStack align="stretch" gap={4}>
                    {!campaignId ? (
                        <Text fontSize="sm" color="text.grey">
                            Zapisz kampanię (np. "Zapisz jako szkic"), żeby móc uruchomić symulację -- potrzebny jest już zapisany config.
                        </Text>
                    ) : (
                        <>
                            <Field label="Telefon lub e-mail testowy">
                                <FormInput value={testContact} onChange={(e) => setTestContact(e.target.value)} placeholder="np. +48 500 000 000 lub test@example.com" />
                            </Field>
                            <Box
                                as="button" type="button" onClick={runSimulation} disabled={isRunning}
                                alignSelf="flex-start" px={5} py={2.5} borderRadius={8} fontSize="sm" fontWeight={600}
                                bg="purple.500" color="white" opacity={isRunning ? 0.6 : 1}
                                display="flex" alignItems="center" gap={2}
                            >
                                {isRunning && <Spinner size="xs" />}
                                ▶ Uruchom symulację
                            </Box>
                        </>
                    )}

                    {result && (
                        <VStack align="stretch" gap={4} pt={2}>
                            <Box>
                                <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                    Dopasowana reguła statusu
                                </Text>
                                {result.matchedStatus ? (
                                    <VStack align="stretch" gap={0}>
                                        <ResultRow label="Dopasowany status" value={statusLabel(result.matchedStatus)} />
                                        <ResultRow label="Akcja agenta" value={result.matchedAgentAction || "--"} />
                                        <ResultRow label="Kanał podstawowy" value={result.resolvedPrimaryChannel ? channelLabel(result.resolvedPrimaryChannel) : "--"} />
                                        <ResultRow label="Kanał dodatkowy" value={result.resolvedFallbackChannel ? channelLabel(result.resolvedFallbackChannel) : "brak"} />
                                        <ResultRow label="Opóźnienie" value={result.delayMinutes != null ? `${result.delayMinutes} min` : "--"} />
                                    </VStack>
                                ) : (
                                    <Text fontSize="sm" color="text.grey">Brak aktywnej reguły statusu w konfiguracji tej kampanii.</Text>
                                )}
                            </Box>

                            {result.startMessageTemplate && (
                                <Box>
                                    <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                        Treść komunikatu startowego
                                    </Text>
                                    <Box bg="bg.dark" borderWidth={1} borderColor="border.primary" borderRadius={8} p={3}>
                                        <Text fontSize="sm" color="text.white" whiteSpace="pre-wrap">{result.startMessageTemplate}</Text>
                                    </Box>
                                </Box>
                            )}

                            <Box>
                                <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={2}>
                                    Mapowanie pól ({(result.leadFieldMappings?.length ?? 0) + (result.customFieldMappings?.length ?? 0)})
                                </Text>
                                {(result.leadFieldMappings?.length || result.customFieldMappings?.length) ? (
                                    <VStack align="stretch" gap={0}>
                                        {(result.leadFieldMappings ?? []).map((m, i) => (
                                            <ResultRow key={`sys-${i}`} label={m.systemField} value={m.sourceColumn} />
                                        ))}
                                        {(result.customFieldMappings ?? []).map((m, i) => (
                                            <ResultRow key={`custom-${i}`} label={m.fieldName} value={m.sourceColumn} />
                                        ))}
                                    </VStack>
                                ) : (
                                    <Text fontSize="sm" color="text.grey">Brak skonfigurowanego mapowania danych leada.</Text>
                                )}
                            </Box>
                        </VStack>
                    )}
                </VStack>
            </CollapsibleSection>
        </VStack>
    );
};

export default Step8FollowUpOutput;
